/**
 * Parses Bokbasen LRM resource URNs such as `bb:work:RD7A7QKTZX3DZ1TFNWP9ZDEF9C`.
 *
 * The identifier is a UUID encoded with Douglas Crockford's Base32
 * (https://www.crockford.com/base32.html): 16 bytes become 26 characters.
 */

/** A parsed Bokbasen LRM resource identifier. */
export interface ResId {
  type: string;
  uuid: string;
}

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const URN_PATTERN = /^bb:([a-z]+):([0-9A-Za-z]{26})$/;

/**
 * Parses a URN into its type and UUID.
 *
 * @throws Error when the URN is not a valid `bb:<type>:<base32>` identifier.
 */
export function parse(urn: string): ResId {
  const match = URN_PATTERN.exec(urn);
  if (match === null) {
    throw new Error(`Invalid URN: '${urn}'`);
  }

  const [, type, identifier] = match;
  return {type, uuid: decodeUuid(identifier)};
}

/** Decodes 26 Crockford Base32 characters into a canonical lower case UUID. */
function decodeUuid(identifier: string): string {
  // Crockford decoding is forgiving: casing is ignored, `I` and `L` mean 1, `O` means 0.
  const normalised = identifier
    .toUpperCase()
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');

  let bits = 0n;
  for (const character of normalised) {
    const value = ALPHABET.indexOf(character);
    if (value < 0) {
      throw new Error(`Bad character: '${character}'`);
    }
    bits = (bits << 5n) | BigInt(value);
  }

  // 26 characters hold 130 bits; the UUID is the leading 128.
  const digits = (bits >> 2n).toString(16).padStart(32, '0');
  return [
    digits.substring(0, 8),
    digits.substring(8, 12),
    digits.substring(12, 16),
    digits.substring(16, 20),
    digits.substring(20),
  ].join('-');
}
